import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";
import { VibeTenantClusterStatusSchema } from "./zadmin-vibe-tenant-cluster-list-item";

/**
 * Hold ONE tenant cluster out of the reconcile sweep, or release it.
 *
 * The reconcile loop is level-triggered and authoritative: it re-converges every
 * cluster with whatever provisioner code the control plane is running. That makes
 * any destructive provisioner change a race between a deploy and a cron — a
 * `pulumi state delete` is undone by the next tick, and a resource removed from
 * the program is deleted on live infrastructure the moment the loop notices. A
 * pause is what lets an operator sequence such a migration by hand.
 */
export const AdminVibeTenantClusterSetReconcilePauseBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /** true = hold out of the sweep, false = release back into it. */
    paused: z.boolean(),
    /**
     * Why the cluster is held — required when pausing, ignored when releasing.
     * Not optional by accident: a pause with no reason is indistinguishable from
     * one someone forgot to clear, which is the failure mode the pause exists to
     * prevent. Mirrored by a DB CHECK that keeps the timestamp and reason paired.
     */
    reason: z.string().min(1).max(500).optional()
  })
  .strict()
  .refine((body) => !body.paused || body.reason !== undefined, {
    message: "reason is required when pausing a cluster",
    path: ["reason"]
  });

export const AdminVibeTenantClusterSetReconcilePauseOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("paused"),
    pausedAt: z.string().datetime(),
    reason: z.string()
  }),
  z.object({ kind: z.literal("resumed") }),
  /** Already in the requested state — idempotent, not an error. */
  z.object({ kind: z.literal("unchanged"), paused: z.boolean() }),
  z.object({ kind: z.literal("not_found") })
]);

/** `AdminVibeTenantClusterSetReconcilePauseOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterSetReconcilePauseOutcomeReadSchema = z.union([
  AdminVibeTenantClusterSetReconcilePauseOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterSetReconcilePauseOutcomeSchema)
  })
]);

/**
 * Force ONE serving tenant cluster to converge.
 *
 * The reconcile loop converges a cluster only from PROVISIONING, UPDATING or
 * DEGRADED; a HEALTHY one is merely observed by a read-only drift preview, and
 * only a drift verdict degrades it. So converging is reachable exclusively
 * through the preview, and a cluster whose preview is wrong — reporting no drift
 * while a declared resource is missing from live infrastructure — is pinned in
 * HEALTHY with no entry point for the loop OR for a human. `provision` refuses a
 * serving cluster (its legal sources are DISABLED_RETAINED and DESTROYED), which
 * left `disable` then `provision` as the only route back: a full teardown and
 * rebuild of a live tenant to repair one resource.
 *
 * This is the entry point, and nothing more than that. It writes the one column
 * the loop reads and lets the loop do the apply, so the converge keeps the cron's
 * crash-safe lease and per-cluster mutex instead of running inside an HTTP
 * request. The repair is therefore asynchronous by construction: the response
 * says a converge WILL happen, never that it has.
 */
export const AdminVibeTenantClusterForceConvergeBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /**
     * Why the operator is forcing this. Required, and for the same reason the
     * pause reason is: it is written into the cluster's operator-visible
     * `statusReason`, so it is what the next person to look at a DEGRADED cluster
     * reads. Without it a forced converge is indistinguishable from a real stack
     * failure at exactly the moment that distinction matters.
     */
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * Discriminated outcome. Every variant is a SUCCESS — each is a true answer to
 * "will this cluster converge now" — so only a persistence fault is an error.
 *
 *   - forced: HEALTHY → DEGRADED. The next reconcile tick runs `pulumi up`.
 *   - already_converging: PROVISIONING / UPDATING / DEGRADED, all of which the
 *     loop already converges every tick. Deliberately NOT re-marked: overwriting
 *     a `statusReason` that explains a real stack failure with "an operator
 *     pressed a button" destroys the field the next debugger reads.
 *   - reconcile_paused: an operator is holding this cluster out of the sweep, so
 *     degrading it would change the row and converge nothing. The variant exists
 *     because that silent no-op is the failure this control could most easily
 *     have shipped with.
 *   - not_converging: a draining or retired cluster has no desired state to be
 *     driven toward.
 */
export const AdminVibeTenantClusterForceConvergeOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("forced"), reason: z.string() }),
  z.object({
    kind: z.literal("already_converging"),
    status: VibeTenantClusterStatusSchema
  }),
  z.object({
    kind: z.literal("reconcile_paused"),
    status: VibeTenantClusterStatusSchema,
    pausedReason: z.string().nullable()
  }),
  z.object({ kind: z.literal("not_converging"), status: VibeTenantClusterStatusSchema }),
  z.object({ kind: z.literal("not_found") })
]);

/** `AdminVibeTenantClusterForceConvergeOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterForceConvergeOutcomeReadSchema = z.union([
  AdminVibeTenantClusterForceConvergeOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterForceConvergeOutcomeSchema)
  })
]);

/**
 * Complete a teardown whose destroy already ran but whose DESTROYED write never
 * landed (NEX-2869).
 *
 * The teardown reaper claims a due cluster (DISABLED_RETAINED → DESTROYING)
 * before running the irreversible `pulumi destroy`, so a crash mid-teardown
 * resumes cleanly. But the FINAL write — DESTROYING → DESTROYED, after the
 * destroy already ran — can itself fail, or the process can die first. On every
 * later tick the reaper's own capture gate then finds no AWS Backup plans left
 * to protect (the destroy removed them) and refuses forever, because it cannot
 * tell "never protected" apart from "already destroyed" once the plans are
 * gone. The cluster sits DESTROYING, permanently `captureBlocked`, with no
 * automated path forward — correctly so, since the automation cannot verify
 * safety here.
 *
 * This is the deliberate bypass: it skips the capture gate entirely and goes
 * straight from a DESTROYING read to DESTROYED, because the human calling it
 * has already done — outside this backend, against real AWS — the
 * verification the capture gate exists to automate and cannot anymore.
 */
export const AdminVibeTenantClusterCompleteTeardownBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /**
     * The operator's own attestation that this org's infrastructure is
     * confirmed gone — HOW they checked, not merely why they're clicking.
     * Required for the same reason `ForceConverge`'s `reason` is: it becomes
     * the cluster's terminal `statusReason`, the only durable record of who
     * signed off on this and how.
     */
    // .trim() BEFORE .min(1): a caller outside the UI (curl, `nexus api`, a
    // future automation) has no client-side trim to lean on, and a
    // whitespace-only value passing this schema would satisfy "required" while
    // carrying no actual attestation once it lands in the terminal statusReason.
    confirmation: z.string().trim().min(1).max(1000)
  })
  .strict();

/**
 * Discriminated outcome of an operator completing a wedged teardown. Mirrors
 * `CompleteTenantClusterTeardownOutcome` in the backend use case.
 *
 *   - destroyed: DESTROYING → DESTROYED. `confirmation` is now the cluster's
 *     terminal `statusReason`.
 *   - already_destroyed: idempotent no-op — either the reaper completed the
 *     destroy on a later tick, or a second operator call raced this one.
 *   - not_destroying: the cluster exists but is not DESTROYING; `status` says
 *     what it actually is. This action only completes an already-claimed
 *     teardown — it is never the thing that starts one.
 *   - not_found: the org has no dedicated cluster row at all.
 */
export const AdminVibeTenantClusterCompleteTeardownOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("destroyed"), confirmation: z.string() }),
  z.object({ kind: z.literal("already_destroyed") }),
  z.object({ kind: z.literal("not_destroying"), status: VibeTenantClusterStatusSchema }),
  z.object({ kind: z.literal("not_found") })
]);

/** `AdminVibeTenantClusterCompleteTeardownOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterCompleteTeardownOutcomeReadSchema = z.union([
  AdminVibeTenantClusterCompleteTeardownOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterCompleteTeardownOutcomeSchema)
  })
]);

export type AdminVibeTenantClusterSetReconcilePauseBody = z.infer<
  typeof AdminVibeTenantClusterSetReconcilePauseBodySchema
>;
export type AdminVibeTenantClusterSetReconcilePauseOutcome = z.infer<
  typeof AdminVibeTenantClusterSetReconcilePauseOutcomeSchema
>;
export type AdminVibeTenantClusterForceConvergeBody = z.infer<
  typeof AdminVibeTenantClusterForceConvergeBodySchema
>;
export type AdminVibeTenantClusterForceConvergeOutcome = z.infer<
  typeof AdminVibeTenantClusterForceConvergeOutcomeSchema
>;
export type AdminVibeTenantClusterCompleteTeardownBody = z.infer<
  typeof AdminVibeTenantClusterCompleteTeardownBodySchema
>;
export type AdminVibeTenantClusterCompleteTeardownOutcome = z.infer<
  typeof AdminVibeTenantClusterCompleteTeardownOutcomeSchema
>;
